import React, {
  useEffect,
  useState
} from "react";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import {
  WysiwygEditor,
  WysiwygEditorRef
} from "src/library/components/WysiwygEditor";
import {
  CheckboxStyled,
  Label
} from "src/library/components/Forms";
import AttachFileButton from "src/library/components/WysiwygEditor/toolbarCustomButtons/AttachFileButton";
import DropZoneMultiFile, { DropZoneRef } from "src/core/shared/components/dropzones/DropZoneMultiFile";
import { FileData } from "src/core/shared/components/files/ListFiles";
import { Text } from "src/library/components/Text";

interface RespondTextAreaProps {
  label: string;
  handleChange: (e: string) => void;
  innerRef?: React.MutableRefObject<WysiwygEditorRef | null>;
  updateAttachments?: (fileKeys: string[]) => void;
  dropzoneRef?: React.MutableRefObject<DropZoneRef | null>;
  subscribed?: boolean;
  updateSubscribed?: React.Dispatch<React.SetStateAction<boolean>>;
  hideSubscribed?: boolean;
}

const RespondTextAreaContainer: React.ReactFCWithChildren<RespondTextAreaProps> = ({
  label,
  handleChange,
  children,
  innerRef,
  updateAttachments,
  dropzoneRef,
  subscribed,
  updateSubscribed,
  hideSubscribed
}) => {
  const [ evidence, setEvidence ] = useState<FileData[]>([]);
  const defaultDropzoneRef = React.useRef<DropZoneRef>(null);
  const componentDropzoneRef = dropzoneRef || defaultDropzoneRef;

  const openFileBrowser = React.useCallback(() => {
    if (componentDropzoneRef?.current) {
      componentDropzoneRef.current.openFileBrowser();
    }
  }, [ componentDropzoneRef ]);

  const toolBartCustomButtons = React.useMemo(() => ([
    <AttachFileButton
      key="AttachFileComponent"
      onClick={() => {
        openFileBrowser();
      }}
    />
  ]), [ openFileBrowser ]);

  useEffect(() => {
    if (updateAttachments) {
      updateAttachments(evidence.map((file: FileData) => file.s3FileKey));
    }
  }, [ evidence ]);

  return (
    <RespondTextAreaWrapper direction="vertical">
      <Label
        for="message"
        label={label}
      />

      {updateAttachments ? (
        <DropZoneMultiFile
          ref={componentDropzoneRef}
          updateFilesToSave={setEvidence}
          type="transparentView"
        >
          <WysiwygEditor
            ref={innerRef}
            toolbarCustomButtons={toolBartCustomButtons}
            handleChange={value => handleChange(value)}
          />
        </DropZoneMultiFile>
      ) : (
        <WysiwygEditor
          ref={innerRef}
          toolbarCustomButtons={toolBartCustomButtons}
          handleChange={value => handleChange(value)}
        />
      )}

      {!hideSubscribed && updateSubscribed && (
        <div className="subscribed-container">
          <Text>
            Subscribe to updates
          </Text>

          <CheckboxStyled
            checked={subscribed}
            onChange={e => updateSubscribed(e.target.checked)}
          />
        </div>
      )}

      <ButtonsFooter direction="horizontal">
        {children}
      </ButtonsFooter>
    </RespondTextAreaWrapper>
  );
};

const ButtonsFooter = styled(Grid.Bounds)`
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.margins.sm};
`;

const RespondTextAreaWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing.margins.sm};
    
    .editor-class {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-top-right-radius: 4px;
      border: 0;
    }
    
    .list-files-ul {
      padding: ${theme.spacing.padding.sm} 20px;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
      background-color: white;
      margin: 0;
    }

    .subscribed-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-bottom: ${theme.spacing.margins.md};
      margin-right: ${theme.spacing.margins.md};

      span {
        margin-right: ${theme.spacing.margins.xs};
      }

      input {
        height: 20px;
        width: 20px;
        border: 1px solid ${theme.colors.primaryDark};
      }
    }
  `}

`;

export default RespondTextAreaContainer;
